import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, {
        signal,
      });

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        {
          signal,
        }
      );

      dialog.addEventListener(
        DialogOpenEvent.eventName,
        this.#handleDialogOpen,
        {
          signal,
        }
      );

      this.addEventListener('click', this.#handleModalClick, {
        signal,
      });
    }

    /*
     * SNEAKYS
     *
     * Eliminado:
     * carga automática de productos vistos recientemente.
     *
     * El buscador debe abrir completamente limpio.
     */
  }

  /**
   * Handles clicks within the predictive search modal
   * to maintain focus on the input.
   *
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  /**
   * Handles the CMD+K key combination.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * When closing the modal:
   * clear search and remove results.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * When opening the modal:
   * do NOT load recommended/recently viewed products.
   */
  #handleDialogOpen = () => {
    /*
     * SNEAKYS
     *
     * Mantener buscador vacío al abrir.
     */
    if (!this.refs.searchInput.value.trim()) {
      this.#clearResults();
    }
  };

  /**
   * Get every interactive search-result item.
   */
  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            'predictive-search-results__wrapper-products'
          )
        ) {
          return Array.from(
            container.querySelectorAll(
              '.predictive-search-results__card'
            )
          );
        }

        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card'
          )
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based.
   *
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) =>
          item.getAttribute('aria-selected') === 'true'
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [
      itemIndex,
      item,
    ] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion()
        ? 'instant'
        : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[
      this.#currentIndex
    ];
  }

  /**
   * Navigate through the predictive search results.
   *
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems?.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;

        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;

        event.preventDefault();

        if (event.shiftKey) {
          this.#currentIndex =
            currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1;
        } else {
          this.#currentIndex =
            currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }

        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;

        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          this.#currentItem
            ?.querySelector('a')
            ?.click();
        } else {
          const searchUrl = new URL(
            Theme.routes.search_url,
            location.origin
          );

          searchUrl.searchParams.set(
            'q',
            this.refs.searchInput.value
          );

          window.location.href =
            searchUrl.toString();
        }

        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   *
   * Kept for compatibility in case another
   * predictive search template references it.
   *
   * @param {Event} event
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add(
        'removing'
      );

      onAnimationEnd(
        recentlyViewedWrapper,
        () => {
          recentlyViewedWrapper.remove();
        }
      );
    }
  }

  /**
   * Public reset action.
   *
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  /**
   * Search handler.
   */
  search = debounce((event) => {
    /*
     * Ignore non-input events.
     */
    if (!event.inputType) return;

    const searchTerm =
      this.refs.searchInput.value.trim();

    this.#isKeyboardNavigation = false;

    /*
     * When input becomes empty:
     * remove all results and do NOT reload
     * recommendations.
     */
    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions.
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector(
          '.predictive-search-results__inner'
        )
        ?.scrollTo(0, 0);

      this.querySelector(
        '.predictive-search-form__content'
      )?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results.
   *
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      Theme.routes.predictive_search_url,
      location.origin
    );

    url.searchParams.set('q', searchTerm);

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    const { predictiveSearchResults } =
      this.refs;

    const abortController =
      this.#createAbortController();

    const deferredPromise =
      SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise:
          deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      )
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({
            totalCount: 0,
          });

          return;
        }

        if (
          abortController.signal.aborted
        ) {
          deferredPromise.reject(
            new Error(
              'Fetch aborted by user'
            )
          );

          return;
        }

        /*
         * IMPORTANT:
         * Only insert results if this response
         * still belongs to the current input.
         *
         * Prevents a late response from restoring
         * results after the user erased the query.
         */
        const currentSearchTerm =
          this.refs.searchInput.value.trim();

        if (
          !currentSearchTerm ||
          currentSearchTerm !== searchTerm
        ) {
          deferredPromise.resolve({
            totalCount: 0,
          });

          return;
        }

        morph(
          predictiveSearchResults,
          resultsMarkup
        );

        this.#resetScrollPositions();

        const resultCount =
          predictiveSearchResults.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card'
          ).length;

        deferredPromise.resolve({
          totalCount: resultCount,
        });
      })
      .catch((error) => {
        deferredPromise.reject(error);

        if (
          abortController.signal.aborted
        ) {
          return;
        }

        throw error;
      });
  }

  /**
   * Hide reset button.
   */
  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  /**
   * Show reset button.
   */
  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Create new AbortController and cancel
   * previous search request.
   */
  #createAbortController() {
    const abortController =
      new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch =
      abortController;

    return abortController;
  }

  /**
   * Remove the current search results.
   *
   * SNEAKYS:
   * This deliberately does NOT load
   * predictive-search-empty.
   */
  #clearResults() {
    const {
      predictiveSearchResults,
    } = this.refs;

    /*
     * Remove keyboard selection state.
     */
    this.#allResultsItems.forEach(
      (item) => {
        item.removeAttribute(
          'aria-selected'
        );

        item.classList.remove(
          'keyboard-focus'
        );
      }
    );

    /*
     * Empty results.
     */
    predictiveSearchResults.innerHTML = '';

    /*
     * Make sure accessibility state
     * reflects no visible results.
     */
    this.refs.searchInput.setAttribute(
      'aria-expanded',
      'false'
    );

    this.#resetScrollPositions();
  }

  /**
   * Reset search completely.
   *
   * BEFORE:
   * Horizon fetched predictive-search-empty
   * and reinserted recommended/recent products.
   *
   * NOW:
   * Empty query = empty results.
   */
  #resetSearch = () => {
    const {
      searchInput,
    } = this.refs;

    /*
     * Cancel any predictive search still running.
     */
    if (this.#activeFetch) {
      this.#activeFetch.abort();

      this.#activeFetch = null;
    }

    /*
     * Reset navigation.
     */
    this.#isKeyboardNavigation = false;

    /*
     * Clear input.
     */
    searchInput.value = '';

    /*
     * Hide "Borrar".
     */
    this.#hideResetButton();

    /*
     * Remove every result.
     */
    this.#clearResults();
  };
}

if (
  !customElements.get(
    'predictive-search-component'
  )
) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}